import { jwtDecode } from "jwt-decode";
import apiClient from "./apiClient";

/**
 * Decode JWT token and check if it's expired
 * @param {string} token - JWT token to decode
 * @returns {Object} - { isValid: boolean, decoded: Object|null, isExpired: boolean }
 */
export const decodeAndValidateToken = (token) => {
  try {
    if (!token) {
      return { isValid: false, decoded: null, isExpired: true };
    }

    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Convert to seconds
    const isExpired = decoded.exp ? decoded.exp < currentTime : true;

    return {
      isValid: !isExpired,
      decoded,
      isExpired,
    };
  } catch (error) {
    console.error("❌ Error decoding token:", error);
    return { isValid: false, decoded: null, isExpired: true };
  }
};

/**
 * Check if token is expired or will expire soon (within 5 minutes)
 * @param {string} token - JWT token to check
 * @returns {boolean} - true if expired or expiring soon
 */
export const isTokenExpiredOrExpiringSoon = (token) => {
  try {
    if (!token) return true;

    const decoded = jwtDecode(token);
    if (!decoded.exp) return true;

    const currentTime = Date.now() / 1000; // Convert to seconds
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    const timeUntilExpiry = decoded.exp - currentTime;

    // Token is expired or will expire within 5 minutes
    return timeUntilExpiry <= fiveMinutes;
  } catch (error) {
    console.error("❌ Error checking token expiry:", error);
    return true;
  }
};

/**
 * Refresh the access token using refresh token and automatically save to sessionStorage
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<Object>} - { accessToken: string, refreshToken: string|null, success: boolean }
 */
export const refreshAccessToken = async (refreshToken) => {
  try {
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    console.log("🔄 Refreshing access token...");

    // Get the old token for debugging
    const oldToken = sessionStorage.getItem("ecoToken");
    const oldRefreshToken = sessionStorage.getItem("ecoRefreshToken");

    console.log("🔍 Before refresh - sessionStorage state:", {
      hasOldToken: !!oldToken,
      hasOldRefreshToken: !!oldRefreshToken,
      refreshTokenMatches: oldRefreshToken === refreshToken,
    });

    const response = await apiClient.post(
      "/auth/refresh",
      {
        refresh_token: refreshToken,
      },
      {
        validateStatus: function (status) {
          // Allow 401 to be caught and handled properly
          return status < 500;
        },
        isAuthorized: false, // refresh token لا يحتاج توكن
      }
    );

    console.log("✅ Token refreshed successfully");

    // Extract new tokens from response
    let newAccessToken = null;
    let newRefreshToken = refreshToken; // Keep old refresh token if new one not provided

    if (response.data.token) {
      newAccessToken = response.data.token;
    } else if (response.data.access_token) {
      newAccessToken = response.data.access_token;
    } else if (response.data.data && response.data.data.token) {
      newAccessToken = response.data.data.token;
    } else if (response.data.data && response.data.data.access_token) {
      newAccessToken = response.data.data.access_token;
    }

    // Check if new refresh token is provided
    if (response.data.refresh_token) {
      newRefreshToken = response.data.refresh_token;
    } else if (response.data.data && response.data.data.refresh_token) {
      newRefreshToken = response.data.data.refresh_token;
    }

    if (!newAccessToken) {
      throw new Error("No access token in refresh response");
    }

    // Validate the new token
    try {
      const { isValid } = decodeAndValidateToken(newAccessToken);
      if (!isValid) {
        console.warn("⚠️ New token appears to be invalid based on JWT decode");
        // Continue anyway - server might have different validation
      }
    } catch (validationError) {
      console.warn(
        "⚠️ Could not validate new token structure:",
        validationError.message
      );
    }

    // 🔥 CRITICAL: Save tokens to sessionStorage IMMEDIATELY
    sessionStorage.setItem("ecoToken", newAccessToken);
    console.log("💾 Saved new access token to sessionStorage");

    if (newRefreshToken && newRefreshToken !== refreshToken) {
      sessionStorage.setItem("ecoRefreshToken", newRefreshToken);
      console.log("💾 Saved new refresh token to sessionStorage");
    }

    // Verify the save was successful
    const savedToken = sessionStorage.getItem("ecoToken");
    const savedRefreshToken = sessionStorage.getItem("ecoRefreshToken");

    console.log("🔍 After save - sessionStorage verification:", {
      savedTokenMatches: savedToken === newAccessToken,
      savedRefreshTokenMatches: savedRefreshToken === newRefreshToken,
      savedTokenLength: savedToken ? savedToken.length : 0,
      savedRefreshTokenLength: savedRefreshToken ? savedRefreshToken.length : 0,
    });

    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      success: true,
    };
  } catch (error) {
    console.error("❌ Error refreshing token:", error);

    if (error.response?.status === 401) {
      const errorMessage =
        error.response.data?.message || "Refresh token invalid";
      console.error("🚨 Refresh token invalid (401):", errorMessage);

      // 🆕 Clear all tokens from sessionStorage on 401
      console.log("🚨 Clearing authentication tokens due to 401 response");
      sessionStorage.removeItem("ecoToken");
      sessionStorage.removeItem("ecoRefreshToken");

      // Also clear any other auth-related items
      sessionStorage.removeItem("refreshTokenInvalid");
      sessionStorage.removeItem("refreshTokenInvalidReason");
      sessionStorage.removeItem("refreshTokenInvalidTime");

      console.log("✅ All tokens cleared from sessionStorage");

      // 🆕 Dispatch event for navigation handling
      console.log("📢 Dispatching auth expiration event");
      const authExpiredEvent = new CustomEvent("authExpired", {
        detail: { reason: "refresh_token_invalid", statusCode: 401 },
      });
      window.dispatchEvent(authExpiredEvent);

      const customError = new Error("REFRESH_TOKEN_INVALID");
      customError.code = "REFRESH_TOKEN_INVALID";
      customError.message = "Session expired. Please login again.";
      customError.originalError = error;
      customError.clearedTokens = true;
      customError.shouldNavigate = true;
      customError.statusCode = 401;
      throw customError;
    }

    const isNetworkError =
      !error.response &&
      (error.code === "ECONNABORTED" ||
        error.message?.includes("timeout") ||
        error.message?.includes("Network"));

    if (isNetworkError) {
      console.warn("⚠️ Network error during refresh, keeping existing tokens");
      const networkError = new Error("NETWORK_ERROR_DURING_REFRESH");
      networkError.code = "NETWORK_ERROR";
      networkError.message = "Network error during refresh";
      throw networkError;
    }

    // Don't clear tokens on other refresh failures - keep the old ones
    console.log(
      "⚠️ Refresh failed - keeping existing tokens in sessionStorage:",
      {
        hasToken: !!sessionStorage.getItem("ecoToken"),
        hasRefreshToken: !!sessionStorage.getItem("ecoRefreshToken"),
      }
    );

    // Re-throw the error so calling code knows refresh failed
    throw error;
  }
};

/**
 * Helper function to manually save tokens to sessionStorage
 * Use this when tokens are received from login or other sources
 */
export const saveTokensToStorage = (accessToken, refreshToken = null) => {
  try {
    if (!accessToken) {
      console.error("❌ Cannot save: No access token provided");
      return false;
    }

    console.log("💾 Manually saving tokens to sessionStorage...");

    sessionStorage.setItem("ecoToken", accessToken);
    console.log("✅ Saved access token");

    if (refreshToken) {
      sessionStorage.setItem("ecoRefreshToken", refreshToken);
      console.log("✅ Saved refresh token");
    } else {
      // If no new refresh token, keep existing one
      const existingRefreshToken = sessionStorage.getItem("ecoRefreshToken");
      if (!existingRefreshToken) {
        console.log("⚠️ No refresh token provided or existing");
      }
    }

    // Verify save
    const savedAccessToken = sessionStorage.getItem("ecoToken");
    const savedRefreshToken = sessionStorage.getItem("ecoRefreshToken");

    console.log("🔍 Manual save verification:", {
      accessTokenSaved: savedAccessToken === accessToken,
      refreshTokenSaved: refreshToken
        ? savedRefreshToken === refreshToken
        : "no refresh token provided",
    });

    return true;
  } catch (error) {
    console.error("❌ Error saving tokens to sessionStorage:", error);
    return false;
  }
};

/**
 * Get current tokens from sessionStorage
 */
export const getTokensFromStorage = () => {
  try {
    const token = sessionStorage.getItem("ecoToken");
    const refreshToken = sessionStorage.getItem("ecoRefreshToken");

    return {
      accessToken: token,
      refreshToken: refreshToken,
      hasTokens: !!(token || refreshToken),
    };
  } catch (error) {
    console.error("❌ Error getting tokens from sessionStorage:", error);
    return { accessToken: null, refreshToken: null, hasTokens: false };
  }
};

/**
 * 🆕 Clear all authentication tokens from sessionStorage
 * This can be called manually or when logging out
 */
export const clearAuthTokens = () => {
  console.log("🧹 Clearing all authentication tokens...");

  sessionStorage.removeItem("ecoToken");
  sessionStorage.removeItem("ecoRefreshToken");
  sessionStorage.removeItem("refreshTokenInvalid");
  sessionStorage.removeItem("refreshTokenInvalidReason");
  sessionStorage.removeItem("refreshTokenInvalidTime");

  console.log("✅ All auth tokens cleared");

  // Dispatch event for navigation handling
  const authClearedEvent = new CustomEvent("authCleared", {
    detail: { reason: "manual_clear" },
  });
  window.dispatchEvent(authClearedEvent);

  return true;
};

/**
 * 🆕 Check if user is authenticated (has valid token)
 * @returns {boolean} - true if user has valid token
 */
export const isAuthenticated = () => {
  try {
    const token = sessionStorage.getItem("ecoToken");
    if (!token) return false;

    const { isValid } = decodeAndValidateToken(token);
    return isValid;
  } catch (error) {
    console.error("❌ Error checking authentication:", error);
    return false;
  }
};

/**
 * 🆕 Setup navigation listener for auth expiration
 * This should be called once in your app initialization
 * @param {Function} navigateCallback - Callback function to navigate to login
 */
export const setupAuthNavigation = (navigateCallback) => {
  if (typeof navigateCallback !== "function") {
    console.warn("⚠️ navigateCallback must be a function");
    return;
  }

  const handleAuthExpired = (event) => {
    console.log("🎯 Auth expiration event received:", event.detail);
    navigateCallback("/");
  };

  const handleAuthCleared = (event) => {
    console.log("🎯 Auth cleared event received:", event.detail);
    navigateCallback("/");
  };

  // Add event listeners
  window.addEventListener("authExpired", handleAuthExpired);
  window.addEventListener("authCleared", handleAuthCleared);

  console.log("✅ Auth navigation listeners set up");

  // Return cleanup function
  return () => {
    window.removeEventListener("authExpired", handleAuthExpired);
    window.removeEventListener("authCleared", handleAuthCleared);
  };
};
